<script setup>
import { DateTime } from 'luxon';
import { useModal, useModalSlot } from 'vue-final-modal';

import { AutoFlow } from '@/services/auto-flow';

import routes from '@/constants/routes';
import { screens } from '@/constants/form-find-tickets';
import analyticEventKeys from '@/constants/analytic-event-keys';

const props = defineProps({
  selectedRouteId: {
    type: Number,
    default: undefined,
  },
  disabledFromStart: {
    type: Boolean,
    default: false,
  },

  mode: {
    type: String,
    default: 'form',
    validator: (mode) => ['form', 'modal'].includes(mode),
  },

  buttonTheme: {
    type: String,
    default: 'secondary-dark',
  },
});
const emit = defineEmits(['submit', 'openScreen', 'closeScreen']);

const searchStore = useSearchStore();

if (process.client && !searchStore.filtersReset) {
  searchStore.resetFilters();
}

const { t } = useI18n();
const route = useRoute();
const localeRoute = useLocaleRoute();
const analytics = useGoogleAnalytics();
const previousSearch = usePreviousSearch();
const { redirectToSearch } = useGoToSearch();

// breakpoint
const breakpoint = useCustomBreakpoints();
const breakpointDownSm = breakpoint.smaller('sm');

// refs
const dateSelectRef = ref(null);
const seatsSelectRef = ref(null);
const departureInputRef = ref(null);
const arrivalInputRef = ref(null);

const isDropdownDisabled = computed(() => breakpointDownSm.value);

onMounted(() => {
  if (!searchStore.isFiltersInitial) {
    AutoFlow.setState(false);
    seatsWasShown.value = true;
    return;
  }

  // search page will update store by itself
  if (route.fullPath.includes(routes.SEARCH)) return;

  // set previous search
  const previousFilters = previousSearch.get();

  if (Object.keys(previousFilters).length > 0) {
    const previousFilter = previousSearch.get();
    const now = DateTime.now();

    const formattedDepartureDate = previousFilter.departureDate
      ? DateTime.fromISO(previousFilter.departureDate)
      : undefined;
    const formattedReturnDate = previousFilter.returnDate
      ? DateTime.fromISO(previousFilter.returnDate)
      : undefined;

    const departureDate =
      formattedDepartureDate && formattedDepartureDate >= now
        ? formattedDepartureDate
        : null;

    let returnDate =
      formattedReturnDate && formattedReturnDate >= now
        ? formattedReturnDate
        : null;

    // return before departure and empty departure validation
    if (
      (returnDate && departureDate && returnDate < departureDate) ||
      !departureDate
    ) {
      returnDate = null;
    }

    const roundTrip = previousFilter.roundTrip && !!returnDate;

    searchStore.updateFilters({
      ...previousFilter,
      departureDate: departureDate ? departureDate.toISODate() : null,
      returnDate: returnDate ? returnDate.toISODate() : null,
      roundTrip,
      children:
        typeof previousFilter.children === 'number'
          ? previousFilter.children
          : 0,
    });

    AutoFlow.setState(false);
    seatsWasShown.value = true;
    searchStore.updateInitialFilters({ ...searchStore.filters });
  }
});

// model
const modelData = computed({
  get() {
    return searchStore.filters;
  },
  set(data) {
    if (!data) return;

    // update filters, reset time
    searchStore.updateFilters({
      ...data,
      departureTime: null,
      returnTime: null,
      departureTimeType: null,
      returnTimeType: null,
    });
    previousSearch.add(data);
  },
});

function submit(saveRouteId) {
  const isFormFilled = checkAndOpenEmptyInputs();

  if (!isFormFilled) return;

  const data = { ...modelData.value };

  if (saveRouteId) data.selectedRouteId = props.selectedRouteId;

  if (props.mode === 'modal') emit('submit');

  redirectToSearch();

  analytics.pushEvent({
    event: analyticEventKeys.CLICK_SEARCH_BUTTON,
    label: `departure: ${modelData.value.departureStation?.id}, arrival: ${modelData.value.arrivalStation?.id}`,
    eventContext: modelData.value.departureDate,
  });
}

function checkAndOpenEmptyInputs() {
  const {
    departureStation,
    arrivalStation,
    departureDate,
    roundTrip,
    returnDate,
  } = modelData.value;

  if (roundTrip && !returnDate && !dateSelectRef.value.visible) {
    dateSelectRef.value.open();
    return false;
  }

  if (!departureStation) {
    openScreen(screens.DEPARTURE);
    return false;
  } else if (!arrivalStation) {
    openScreen(screens.ARRIVAL);
    return false;
  } else if (!seatsWasShown.value && !breakpointDownSm.value) {
    openScreen(screens.SEATS);
    return false;
  } else if (!departureDate) {
    openScreen(screens.DATE);
    return false;
  } else if (roundTrip && !returnDate) {
    openScreen(screens.DATE);
    return false;
  }

  return true;
}

const showSwapButton = computed(
  () => !!modelData.value.departureStation && !!modelData.value.arrivalStation,
);
function swapStations() {
  const newArrival = modelData.value.departureStation;

  modelData.value = {
    departureStation: modelData.value.arrivalStation,
    arrivalStation: newArrival,
  };

  analytics.pushEvent({
    event: analyticEventKeys.SEARCH_FORM_INTERACTION,
    label: `swap stations`,
  });
}

function reset() {
  searchStore.resetFilters();
  previousSearch.clear();
  seatsWasShown.value = false;
  AutoFlow.setState(true);

  analytics.pushEvent({
    event: analyticEventKeys.SEARCH_FORM_INTERACTION,
    label: `clear form`,
  });
}

// location
const locationTypes = {
  departure: 'departure',
  arrival: 'arrival',
};

function handleLocationUpdate(data, type) {
  if (!data) return;

  const formattedData = { ...modelData.value };
  const {
    departureStation: filtersDepartureStation,
    arrivalStation: filtersArrivalStation,
  } = modelData.value;

  const modelKey =
    type === locationTypes.departure ? 'departureStation' : 'arrivalStation';
  const oppositeModelKey =
    type === locationTypes.departure ? 'arrivalStation' : 'departureStation';

  const filtersLocation =
    type === locationTypes.departure
      ? filtersDepartureStation
      : filtersArrivalStation;
  const filtersOppositeLocation =
    type === locationTypes.departure
      ? filtersArrivalStation
      : filtersDepartureStation;

  // swap stations or save
  if (filtersOppositeLocation && data?.id === filtersOppositeLocation.id) {
    [formattedData[modelKey], formattedData[oppositeModelKey]] = [
      data,
      filtersLocation,
    ];
  } else {
    formattedData[modelKey] = data;
  }

  modelData.value = { ...formattedData };
  goToFlow();
}

function getOppositeLocationId(type) {
  return type !== 'departure'
    ? modelData.value.departureStation?.id
    : undefined;
}

// Round trip toggle
watch(
  () => modelData.value.roundTrip,
  (roundTrip) => {
    if (breakpointDownSm.value) return;

    const { returnDate } = modelData.value;

    if (
      roundTrip &&
      !returnDate &&
      dateSelectRef.value &&
      !dateSelectRef.value.visible
    ) {
      dateSelectRef.value.open();
    }
  },
);

// time modal
const showTimeModal = ref(false);

const timeModal = useModal({
  component: resolveComponent('LazyModalsTime'),
  keepAlive: true,
  attrs: {
    filters: modelData.value,
    'onUpdate:filters': (value) => {
      modelData.value = value;
    },
    show: false,
    specificReset: false,
    onClosed: closeTimeModal,
  },
});

function openTimeModal() {
  showTimeModal.value = true;

  analytics.pushEvent({
    event: analyticEventKeys.SEARCH_FORM_INTERACTION,
    label: `screen: time`,
  });
}

function closeTimeModal() {
  showTimeModal.value = false;
}

watch(showTimeModal, (showTimeModal) => {
  if (!breakpointDownSm.value) return;

  nextTick(() => {
    timeModal.patchOptions({ attrs: { show: showTimeModal } });
  });

  if (showTimeModal) {
    timeModal.open();
  } else {
    timeModal.close();
  }
});

function clearTime() {
  modelData.value = {
    departureTimeType: null,
    departureTime: null,
    returnTimeType: null,
    returnTime: null,
  };

  timeModal.patchOptions({
    attrs: { specificReset: !timeModal.options.attrs.specificReset },
  });

  analytics.pushEvent({
    event: analyticEventKeys.SEARCH_FORM_INTERACTION,
    label: `clear time`,
  });
}

// screens
const activeScreen = ref(screens.SEARCH);
const seatsWasShown = ref(route.name === localeRoute('slug').name);

function openScreen(screen) {
  if (!breakpointDownSm.value) {
    switch (true) {
      case screen === screens.DEPARTURE && !!departureInputRef.value:
        departureInputRef.value.focusInput();
        break;
      case screen === screens.ARRIVAL && !!arrivalInputRef.value:
        arrivalInputRef.value.focusInput();
        break;
      case screen === screens.DATE:
        dateSelectRef.value.open();
        break;
      case screen === screens.SEATS:
        seatsWasShown.value = true;
        seatsSelectRef.value.open();
        break;
    }

    return;
  }

  activeScreen.value = screen;
  AutoFlow.init(screen);

  emitScreenChange(screen);

  if (screen !== screens.SEARCH && !showOverlay.value)
    setTimeout(() => (showOverlay.value = true), 500);

  analytics.pushEvent({
    event: analyticEventKeys.SEARCH_FORM_INTERACTION,
    label: `screen: ${screen}`,
  });
}
function closeScreen() {
  showOverlay.value = false;
  activeScreen.value = screens.SEARCH;
  emitScreenChange(screens.SEARCH);
}

function emitScreenChange(screen) {
  if (props.mode !== 'modal') return;

  if (screen !== screens.SEARCH) setTimeout(() => emit('openScreen'), 300);
  else emit('closeScreen');
}

// auto flow
const isAutoFlowStart = computed(() => AutoFlow.isStart(activeScreen.value));
const isAutoFlowFinish = computed(() => AutoFlow.isFinish(activeScreen.value));
const isAutoFlowRunning = computed(() => AutoFlow.getState());

function goToFlow() {
  if (isAutoFlowFinish.value && breakpointDownSm.value) {
    submit();
  } else {
    if (breakpointDownSm.value) {
      activeScreen.value = AutoFlow.next(activeScreen.value);
      emitScreenChange(activeScreen.value);
    } else {
      checkAndOpenEmptyInputs();
    }
  }
}

function goBackToFlow() {
  if (isAutoFlowStart.value) {
    closeScreen();
  } else {
    activeScreen.value = AutoFlow.prev(activeScreen.value);
  }
}

// screen modal
const screenModal = useModal({
  component: resolveComponent('ModalFullscreen'),
  attrs: { contentTransition: 'slide-right-to-left' },
});

watch(activeScreen, async (activeScreen, prevActiveScreen) => {
  if (activeScreen === screens.SEARCH && !searchStore.isFiltersInitial) {
    AutoFlow.setState(false);
  }

  if (!breakpointDownSm.value) return;

  if (activeScreen !== screens.SEARCH) {
    if (prevActiveScreen !== screens.SEARCH) {
      await screenModal.close();
    }

    screenModal.patchOptions({
      slots: {
        default: useModalSlot({
          component: getScreenComponent(activeScreen),
          attrs: {
            ...getScreenAttributes(activeScreen),
          },
        }),
      },
    });

    return screenModal.open();
  } else {
    showOverlay.value = false;
    return screenModal.close();
  }
});

// screen components
function getScreenComponent(activeScreen) {
  switch (activeScreen) {
    case screens.DEPARTURE:
    case screens.ARRIVAL:
      return resolveComponent('FormsSearchScreensStation');
    case screens.DATE:
      return resolveComponent('FormsSearchScreensDate');
    case screens.SEATS:
      return resolveComponent('FormsSearchScreensSeats');
  }
}
const showOverlay = ref(false);

function getScreenAttributes(activeScreen) {
  const attributes = {
    modelValue: modelData.value,
    'onUpdate:modelValue': (value) => {
      modelData.value = value;
    },

    withBack: !isAutoFlowStart.value,
    onBack: goBackToFlow,
    onSuccess: goToFlow,
    onClose: closeScreen,
  };

  switch (activeScreen) {
    case screens.DEPARTURE:
      attributes.type = 'departure';
      attributes.withAutofocus = props.mode === 'form';
      break;
    case screens.ARRIVAL:
      attributes.type = 'arrival';
      attributes.withAutofocus = props.mode === 'form';
      break;
    case screens.DATE:
      attributes.isFlowFinish = isAutoFlowFinish.value;
      attributes.isFlowRunning = isAutoFlowRunning.value;
      attributes.onTimeClick = openTimeModal;
      attributes.onTimeClear = clearTime;
      attributes.onClose = () => {
        if (modelData.value.roundTrip && !modelData.value.returnDate) {
          modelData.value.roundTrip = false;
        }
        closeScreen();
      };
      break;
    case screens.SEATS:
      attributes.isFlowFinish = isAutoFlowFinish.value;
      attributes.isFlowRunning = isAutoFlowRunning.value;
      break;
  }

  return attributes;
}

// button
const isButtonDisabled = computed(() => {
  const normalizeDate = (date) =>
    date ? DateTime.fromISO(date).toISO() : null;

  const initialFieldsToCompare = {
    ...searchStore.initialFilters,
    departureStation: null,
    arrivalStation: null,
    departureId: searchStore.initialFilters?.departureStation?.id,
    arrivalId: searchStore.initialFilters?.arrivalStation?.id,
    departureDate: normalizeDate(searchStore.initialFilters?.departureDate),
    departureTime: searchStore.initialFilters?.departureTime || undefined,
    departureTimeType:
      searchStore.initialFilters?.departureTimeType || undefined,
    returnDate: normalizeDate(searchStore.initialFilters?.returnDate),
    returnTime: searchStore.initialFilters?.returnTime || undefined,
    returnTimeType: searchStore.initialFilters?.returnTimeType || undefined,
  };

  const modelFieldsToCompare = {
    ...modelData.value,
    departureStation: null,
    arrivalStation: null,
    departureId: modelData.value?.departureStation?.id,
    arrivalId: modelData.value?.arrivalStation?.id,
    departureDate: normalizeDate(modelData.value?.departureDate),
    departureTime: modelData.value?.departureTime || undefined,
    departureTimeType: modelData.value?.departureTimeType || undefined,
    returnDate: normalizeDate(modelData.value?.returnDate),
    returnTime: modelData.value?.returnTime || undefined,
    returnTimeType: modelData.value?.returnTimeType || undefined,
    roundTrip: modelData.value?.roundTrip && !!modelData.value?.returnDate,
  };

  return (
    props.disabledFromStart &&
    isObjectsSame(initialFieldsToCompare, modelFieldsToCompare)
  );
});

// departure focus
const searchFormStore = useSearchFormStore();

watch(() => searchFormStore.focusDepartureFlag, focusDeparture);

function focusDeparture() {
  if (!departureInputRef.value) return;
  departureInputRef.value.focusInput();
}

// date
function handleDateClick(type) {
  if (!modelData.value.roundTrip && breakpointDownSm.value) {
    modelData.value.roundTrip = type === 'return';
  }

  openScreen(screens.DATE);
}
</script>

<template>
  <div
    class="bundles-search"
    :class="[
      `bundles-search--mode--${mode}`,
      { 'bundles-search--with-disabling': disabledFromStart },
    ]"
  >
    <div v-show="showOverlay" class="bundles-search__overlay hidden-desktop" />

    <Transition name="fade">
      <button
        v-show="!searchStore.isFiltersInitial && mode !== 'modal'"
        type="button"
        class="bundles-search__button bundles-search__button--clear"
        @click="reset"
      >
        <CIcon
          class="bundles-search__icon bundles-search__icon--trash"
          name="trash"
        />
        <span class="bundles-search__font bundles-search__font--clear">
          {{ t('form.clearSearch') }}
        </span>
      </button>
    </Transition>

    <div class="bundles-search__container">
      <button
        v-if="showSwapButton"
        type="button"
        class="bundles-search__button bundles-search__button--swap"
        @click="swapStations"
      >
        <CIcon
          class="bundles-search__icon bundles-search__icon--swap"
          name="return-2"
        />
      </button>

      <div class="bundles-search__inputs">
        <BundlesStationSearch
          ref="departureInputRef"
          class="bundles-search__field bundles-search__field--location"
          :placeholder="t('form.from')"
          :opposite-location-id="getOppositeLocationId('departure')"
          name="arrival"
          icon-name="departure"
          rounded-left
          :disabled="isDropdownDisabled"
          :model-value="modelData.departureStation"
          @click="openScreen(screens.DEPARTURE)"
          @update:model-value="
            (data) => handleLocationUpdate(data, locationTypes.departure)
          "
        />

        <BundlesStationSearch
          ref="arrivalInputRef"
          class="bundles-search__field bundles-search__field--location"
          :placeholder="t('form.to')"
          :opposite-location-id="getOppositeLocationId('arrival')"
          name="departure"
          icon-name="arrival"
          :disabled="isDropdownDisabled"
          :model-value="modelData.arrivalStation"
          @click="openScreen(screens.ARRIVAL)"
          @update:model-value="
            (data) => handleLocationUpdate(data, locationTypes.arrival)
          "
        />

        <UiSeatsSelect
          ref="seatsSelectRef"
          v-model="modelData"
          class="bundles-search__field bundles-search__field--seats"
          :disabled="isDropdownDisabled"
          @click="openScreen(screens.SEATS)"
          @set="checkAndOpenEmptyInputs"
        />

        <UiDateSelect
          ref="dateSelectRef"
          v-model="modelData"
          class="bundles-search__field bundles-search__field--date"
          :is-form-button-disabled="isButtonDisabled"
          :disabled="isDropdownDisabled"
          rounded
          @click="handleDateClick"
          @search="submit"
        />
      </div>

      <UiButton
        class="bundles-search__button bundles-search__button--search"
        :theme="buttonTheme"
        :disabled="isButtonDisabled"
        @click="submit"
      >
        <div class="bundles-search__wrapper">
          <CIcon
            class="bundles-search__icon bundles-search__icon--search"
            name="search-2"
          />

          <span class="bundles-search__font bundles-search__font--search">
            {{ t('form.search') }}
          </span>
        </div>
      </UiButton>
    </div>
  </div>
</template>
